import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import axios from 'axios';
import styles from './MercenaryProfile.module.css';
import { useAgeStageLabel } from '../../../hooks/useAgeStageLabel';

interface Weapon {
  id: number | null;
  name: string;
  minDamage: number;
  maxDamage: number;
  critChance: number;
  critDamage: number;
  weaponType: string;
  weight?: number;
  skillRec?: number;
  blockValue?: number;
  breakValue?: number;
  runeSlots?: string[];
}

interface MercenaryLive {
  id: number;
  name: string;
  race: string;
  sex: string;
  avatar: string;
  age?: number;
  level: number;
  title: string;
  experience: number;
  nextExperience: number;
  previousExperience: number;
  restedExp: number;
  health: number;
  maxHealth: number;
  rounds: number;
  maxRounds: number;
  form: number;
  strength: number;
  endurance: number;
  initiative: number;
  dodge: number;
  totalCritChance: number;
  totalCritDamage: number;
  racialCritDamage?: number;
  learning: number;
  luck: number;
  discipline: number;
  leadership: number;
  provocation: number;
  weaponSkills: { [key: string]: number };
  weapons: {
    mainHand?: Weapon | null;
    offHand?: Weapon | null;
    twoHand?: Weapon | null;
    shield?: Weapon | null;
  };
  silver: number;
  reputation: number;
  primarySkillPoints: number;
  secondarySkillPoints: number;
  isHospitalized?: boolean;
  // new flag indicates the mercenary has previously been saved by Freya
  savedByFreya?: boolean;
  armor?: { id?: number; name?: string; slot?: string; armorType?: string; absorption?: number; absorptionPercentage?: number; minAbsorption?: number; maxAbsorption?: number; weight?: number; runeSlots?: string[] }[];
  rawStats?: {
    strength: number;
    endurance: number;
    initiative: number;
    dodge: number;
    learning: number;
    luck: number;
    discipline: number;
    health: number;
    leadership: number;
    provocation: number;
  };
  gearStats?: {
    strength?: number;
    endurance?: number;
    initiative?: number;
    dodge?: number;
    learning?: number;
    luck?: number;
    discipline?: number;
    health?: number;
    leadership?: number;
    provocation?: number;
  };
  rawWeaponSkills?: {
    axe: number;
    sword: number;
    mace: number;
    staff: number;
    dagger: number;
    chain: number;
    shield: number;
  };
  equippedRunes?: Record<string, Record<number, string>>;
  raceBonus?: {
    strength: number;
    endurance: number;
    initiative: number;
    avoidance: number;
    learningCapacity: number;
    luck: number;
    discipline: number;
    health: number;
    leadership: number;
    provocation: number;
    weaponSkillBonus?: {
      axe: number;
      sword: number;
      mace: number;
      staff: number;
      dagger: number;
      chain: number;
      shield: number;
    };
  };
}

interface ProfileStats {
  userId: number;
  name: string;
  race: string;
  age: number;
  sex: string;
  level: number;
  reputation: number;
  avatar: string;
  title: string;
  kills: number;
  alignment?: string;
  savedByFreya?: boolean;
  stats: {
    winsTournament: number;
    lossTournament: number;
    tournamentFirstPlace: number;
    tournamentSecondPlace: number;
    tournamentThirdPlace: number;
    winsDuel: number;
    lossDuel: number;
    winsTeamBattle: number;
    lossTeamBattle: number;
    winsPvE: number;
    lossPvE: number;
    winsContract: number;
    lossContract: number;
    highestDamageDealt: number;
    mostDamageDone: number;
    mostDamageTaken: number;
    mostCriticalHits: number;
    mostDodgedAttacks: number;
    mostParries: number;
  } | null;
}

const ALIGNMENT_COLOR: Record<string, string> = {
  Good: '#5cb87a', Neutral: 'var(--color-gold)', Evil: '#c0392b',
};
const ALIGNMENT_GLYPH: Record<string, string> = {
  Good: '✦', Neutral: '◆', Evil: '☠',
};
const WEAPON_SLOT_LABEL: Record<string, string> = {
  mainHand: 'Main Hand', offHand: 'Off Hand', twoHand: 'Two-Hand', shield: 'Shield',
};
const ARMOR_SLOT_LABEL: Record<string, string> = {
  head: 'Head', chest: 'Chest', legs: 'Legs', feet: 'Feet', hands: 'Hands',
  neck: 'Neck', ring: 'Ring', earring: 'Earring', finger: 'Finger', accessory: 'Accessory',
};
const RACE_BASE_WEIGHT: Record<string, number> = {
  goblin: 2, elf: 0, human: 5, dwarf: 6, orc: 7, troll: 12,
};
const SKILL_LABEL: Record<string, string> = {
  axe: 'Axe', sword: 'Sword', mace: 'Mace', staff: 'Staff',
  dagger: 'Dagger', chain: 'Chain', shield: 'Shield', unarmed: 'Unarmed',
};

function StatBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={styles.barTrack}>
      <div className={styles.barFill} style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function SkillBar({ value, max = 100 }: { value: number; max?: number }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className={styles.skillBarTrack}>
      <div className={styles.skillBarFill} style={{ width: `${pct}%` }} />
      <span className={styles.skillBarValue}>{Math.round(value)}</span>
    </div>
  );
}

function wl(wins: number, losses: number) {
  const total = wins + losses;
  const rate = total > 0 ? Math.round((wins / total) * 100) : 0;
  return { wins, losses, rate };
}

const ProfileView: React.FC = () => {
  const [live, setLive] = useState<MercenaryLive | null>(null);
  const [profile, setProfile] = useState<ProfileStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mercenaryId } = useMercenary();
  const navigate = useNavigate();
  const ageStageLabel = useAgeStageLabel(live?.race ?? profile?.race, live?.age ?? profile?.age);
  const rawAge = live?.age ?? profile?.age;
  const ageDisplay = rawAge != null
    ? ageStageLabel ?? `${rawAge}d`
    : null;
  const ageTooltip = rawAge != null
    ? `${rawAge - 20} days in the city · Total age: ${rawAge}`
    : null;

  useEffect(() => {
    if (!mercenaryId) return;
    const fetchData = async () => {
      try {
        const [liveRes, profileRes] = await Promise.all([
          axios.get(`/api/mercenary/${mercenaryId}`, { withCredentials: true }),
          axios.get(`/api/mercenary/profile/${mercenaryId}`, { withCredentials: true }),
        ]);
        setLive(liveRes.data);
        setProfile(profileRes.data);
        if (liveRes.data.isHospitalized) navigate(`/hospital/${mercenaryId}`);
      } catch (err) {
        setError('Failed to load mercenary data.');
      }
    };
    fetchData();
  }, [mercenaryId, navigate]);

  if (error) return <div className={styles.errorState}>{error}</div>;
  if (!live) return (
    <div className={styles.loadingState}>
      <div className={styles.loadingSpinner} />
      <p>Loading mercenary…</p>
    </div>
  );

  const race = live.race.toLowerCase();
  const sex  = live.sex.toLowerCase();
  const avatarSrc = `/img/avatars/${race}.${sex}.${live.avatar}.png`;

  const xpMin   = live.previousExperience ?? 0;
  const xpMax   = live.nextExperience ?? 1;
  const xpCur   = live.experience ?? 0;
  const xpRange = xpMax - xpMin;
  const xpPct   = xpRange > 0 ? Math.min(100, Math.round(((xpCur - xpMin) / xpRange) * 100)) : 0;

  const alignment  = profile?.alignment ?? 'Neutral';
  const alignColor = ALIGNMENT_COLOR[alignment] ?? ALIGNMENT_COLOR.Neutral;
  const alignGlyph = ALIGNMENT_GLYPH[alignment] ?? '◆';

  const s = profile?.stats;
  const tournament = wl(s?.winsTournament ?? 0, s?.lossTournament ?? 0);
  const duel       = wl(s?.winsDuel ?? 0, s?.lossDuel ?? 0);
  const team       = wl(s?.winsTeamBattle ?? 0, s?.lossTeamBattle ?? 0);
  const pve        = wl(s?.winsPvE ?? 0, s?.lossPvE ?? 0);
  const contract   = wl(s?.winsContract ?? 0, s?.lossContract ?? 0);

  const equippedSlots = (['mainHand', 'offHand', 'twoHand', 'shield'] as const).filter(
    (slot) => live.weapons[slot] != null
  );

  const repColor = live.reputation >= 0 ? '#5cb87a' : '#c0392b';
  const repSign  = live.reputation > 0 ? '+' : '';

  const raceWeight   = RACE_BASE_WEIGHT[live.race.toLowerCase()] ?? 0;
  const armorWeight  = (live.armor ?? []).reduce((s, a) => s + (a.weight ?? 0), 0);
  const weaponWeight = (['mainHand', 'offHand', 'twoHand', 'shield'] as const)
    .reduce((s, slot) => s + (live.weapons[slot]?.weight ?? 0), 0);
  const rawWeight     = raceWeight + armorWeight + weaponWeight;
  const strMitigation = Math.min((live.strength ?? 0) / 10, 25);
  const effectiveWeight = Math.max(0, rawWeight - strMitigation);

  // --- Combat estimates (Normal tactics, attacker skill factor = 1.0) ---
  const estBattleRounds = Math.max(4, Math.floor(live.endurance / 3.5));
  const dodgeWeightPenalty = Math.min(effectiveWeight / 25, 0.85);
  const blockWeightPenalty = Math.min(effectiveWeight / 60, 0.25);
  const rawDodgeChance = (0.01 + 0.65 * (live.dodge / 500)) * (1 - dodgeWeightPenalty);
  const estDodgePct = Math.round(Math.min(0.70, Math.max(0.01, rawDodgeChance)) * 100);

  const shield = live.weapons.shield;
  let estBlockPct: number | null = null;
  if (shield) {
    const shieldSkill = live.weaponSkills?.shield ?? 0;
    const skillRec    = shield.skillRec ?? 100;
    const sf          = Math.min(2.0, shieldSkill / skillRec);
    const baseBlock   = sf >= 1 ? 0.30 + 0.40 * (sf - 1) : 0.05 + 0.25 * sf;
    const adjBlock    = baseBlock * (1 - blockWeightPenalty * 1.5);
    estBlockPct       = Math.round(Math.min(0.70, Math.max(0.05, adjBlock)) * 100);
  }

  return (
    <div className={styles.container}>

      {/* BANNER */}
      <div className={styles.banner}>
        <img
          src={avatarSrc} alt={live.name} className={styles.bannerAvatar}
          onError={(e) => { (e.target as HTMLImageElement).src = '/img/no_img.png'; }}
        />
        <div className={styles.bannerInfo}>
          <div className={styles.bannerNameRow}>
            <h1 className={styles.bannerName}>{live.name}</h1>
            {live.title && <span className={styles.titleBadge}>{live.title}</span>}
          </div>
          <div className={styles.bannerMeta}>
            <span className={styles.metaPill}>{live.race}</span>
            <span className={styles.metaPill}>{live.sex}</span>
            {ageDisplay && (
              <span
                className={`${styles.metaPill} ${styles.agePill}`}
                data-tooltip={ageTooltip ?? undefined}
              >
                {ageDisplay}
              </span>
            )}
            <span className={styles.metaPill} style={{ color: alignColor, borderColor: alignColor }}>
              {alignGlyph} {alignment}
            </span>
            {(profile?.kills ?? 0) > 0 && (
              <span className={styles.metaPill} style={{ color: '#c0392b', borderColor: '#c0392b' }}>
                {profile!.kills} kills
              </span>
            )}
            {live.savedByFreya && (
              <span className={styles.metaPill} style={{ color: '#dee03c', borderColor: '#e4e129' }}>
                Freya's Grace
              </span>
            )}
          </div>
          <div className={styles.xpRow}>
            <span className={styles.xpLabel}>Lv.{live.level}</span>
            <div className={styles.xpTrack}>
              <div className={styles.xpFill} style={{ width: `${xpPct}%` }} />
              {live.restedExp > 0 && (
                <div className={styles.xpRested} style={{
                  left: `${xpPct}%`,
                  width: `${Math.min(100 - xpPct, Math.round((live.restedExp / xpRange) * 100))}%`
                }} />
              )}
              <span className={styles.xpText}>{xpCur.toLocaleString()} / {xpMax.toLocaleString()} XP</span>
            </div>
            <span className={styles.xpPct}>{xpPct}%</span>
          </div>
          {(live.primarySkillPoints > 0 || live.secondarySkillPoints > 0) && (
            <div className={styles.spAlert}>
              Unspent skill points - visit Advancement to level up!
            </div>
          )}
        </div>
        <div className={styles.resourceCluster}>
          <div className={styles.resourcePill}>
            <span className={styles.resourceValue}>{live.silver.toLocaleString()}</span>
            <span className={styles.resourceLabel}>Silver</span>
          </div>
          <div className={styles.resourcePill}>
            <span className={styles.resourceValue} style={{ color: repColor }}>{repSign}{live.reputation}</span>
            <span className={styles.resourceLabel}>Reputation</span>
          </div>
        </div>
      </div>

      {/* MAIN BODY */}
      <div className={styles.body}>

        {/* LEFT */}
        <div className={styles.leftCol}>
          <div className={styles.panel}>
            <div className={styles.panelTitle}>Equipment</div>
            {(() => {
              const SLOT_ORDER = [
                'mainHand', 'offHand', 'twoHand', 'shield',
                'Head', 'Shoulders', 'Torso', 'Hands', 'Legs', 'Feet',
                'Neck', 'Cloak', 'Amulet', 'Armring', 'Ring',
              ];

              type EquipEntry =
                | { kind: 'weapon'; slotKey: string; label: string; w: typeof live.weapons[keyof typeof live.weapons] }
                | { kind: 'armor';  slotKey: string; label: string; piece: NonNullable<typeof live.armor>[number]; idx: number };

              const entries: EquipEntry[] = [];
              (['mainHand', 'offHand', 'twoHand', 'shield'] as const).forEach((slot) => {
                if (live.weapons[slot]) entries.push({ kind: 'weapon', slotKey: slot, label: WEAPON_SLOT_LABEL[slot], w: live.weapons[slot] });
              });
              (live.armor ?? []).forEach((piece, idx) => {
                const slotKey = piece.slot ?? '';
                entries.push({ kind: 'armor', slotKey, label: ARMOR_SLOT_LABEL[slotKey] ?? slotKey, piece, idx });
              });
              entries.sort((a, b) => {
                const ai = SLOT_ORDER.indexOf(a.slotKey);
                const bi = SLOT_ORDER.indexOf(b.slotKey);
                return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
              });

              if (entries.length === 0) return <p className={styles.emptyNote}>Nothing equipped</p>;

              return entries.map((entry) => {
                if (entry.kind === 'weapon') {
                  const w = entry.w!;
                  const wRuneSlots: string[] = w.runeSlots || [];
                  const wSocketed = live.equippedRunes?.[entry.slotKey] ?? {};
                  return (
                    <div key={entry.slotKey} className={styles.equipCard}>
                      <div className={styles.equipSlotLabel}>{entry.label}</div>
                      <div className={styles.equipName}>{w.name}</div>
                      <div className={styles.equipMeta}>
                        {entry.slotKey === 'shield' ? (
                          <>
                            {w.blockValue != null && <span>Block: {w.blockValue}</span>}
                            {w.breakValue != null && <span>Durability: {w.breakValue}</span>}
                          </>
                        ) : (
                          <>
                            <span>{w.weaponType}</span>
                            <span>{w.minDamage}–{w.maxDamage} dmg</span>
                            {w.critChance > 0 && <span>+{w.critChance}% crit</span>}
                          </>
                        )}
                      </div>
                      {wRuneSlots.length > 0 && (
                        <div className={styles.equipMeta}>
                          {wRuneSlots.map((color, idx) => {
                            const socketed = wSocketed[idx];
                            return (
                              <span key={idx} title={socketed ?? `Empty ${color} socket`}
                                style={{ color: socketed ? '#c0f1b3' : 'var(--color-text-secondary)' }}>
                                {socketed ? `◆ ${socketed}` : `◇ ${color}`}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  const { piece, idx } = entry;
                  const hasAbsorption = (piece.absorption ?? 0) > 0 || (piece.minAbsorption ?? 0) > 0;
                  const pRuneSlots: string[] = piece.runeSlots || [];
                  const pSocketed = live.equippedRunes?.[piece.slot ?? ''] ?? {};
                  return (
                    <div key={piece.id ?? idx} className={styles.equipCard}>
                      <div className={styles.equipSlotLabel}>{entry.label}</div>
                      <div className={styles.equipName}>{piece.name}</div>
                      <div className={styles.equipMeta}>
                        {hasAbsorption && (
                          piece.minAbsorption != null && piece.maxAbsorption != null && piece.minAbsorption !== piece.maxAbsorption
                            ? <span>Absorb: {piece.minAbsorption}–{piece.maxAbsorption}</span>
                            : <span>Absorb: {piece.absorption ?? piece.minAbsorption}</span>
                        )}
                        {(piece.absorptionPercentage ?? 0) > 0 && (
                          <span>{(piece.absorptionPercentage! * 100).toFixed(0)}% absorb</span>
                        )}
                      </div>
                      {pRuneSlots.length > 0 && (
                        <div className={styles.equipMeta}>
                          {pRuneSlots.map((color, i) => {
                            const socketed = pSocketed[i];
                            return (
                              <span key={i} title={socketed ?? `Empty ${color} socket`}
                                style={{ color: socketed ? '#c0f1b3' : 'var(--color-text-secondary)' }}>
                                {socketed ? `◆ ${socketed}` : `◇ ${color}`}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
              });
            })()}
            <div className={styles.weightSection}>
              <div className={styles.weightRow}>
                <span className={styles.weightLabel}>Total Weight</span>
                <span className={styles.weightValues}>{rawWeight}</span>
              </div>
              <div className={styles.weightRow}>
                <span className={styles.weightLabel}>After Strength</span>
                <span className={styles.weightValues}>{effectiveWeight.toFixed(1)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className={styles.rightCol}>

          {/* Vitals */}
          <div className={styles.panel}>
            <div className={styles.panelTitle}>Vitals</div>
            {[
              { label: 'Health',  val: live.health,  max: live.maxHealth, color: 'linear-gradient(90deg,#8b1a1a,#e74c3c)', ic: '#e74c3c' },
              { label: 'Rounds', val: live.rounds,  max: live.maxRounds, color: 'linear-gradient(90deg,#1a4a7a,#3498db)', ic: '#3498db' },
              { label: 'Form',    val: live.form,    max: 100,            color: 'linear-gradient(90deg,#1a6b3e,#2ecc71)', ic: '#2ecc71' },
            ].map(({ label, val, max, color, ic }) => (
              <div key={label} className={styles.vitalRow}>
                <div className={styles.vitalLabel}>
                  <span style={{ color: ic }}>{label}</span>
                </div>
                <StatBar value={val} max={max} color={color} />
                <span className={styles.vitalValue}>
                  {label === 'Form' ? `${val}%` : `${val} / ${max}`}
                </span>
              </div>
            ))}
          </div>

          {/* Stat Breakdown */}
          {live.rawStats && live.raceBonus && (
            <div className={styles.panel}>
              <div className={styles.panelTitle}>Stat Breakdown</div>
              <table className={styles.statBreakdownTable}>
                <thead>
                  <tr>
                    <th className={styles.sbHeader}>Stat</th>
                    <th className={styles.sbHeader}>Points</th>
                    <th className={styles.sbHeader}>Racial</th>
                    <th className={styles.sbHeader}>Gear</th>
                    <th className={styles.sbHeader}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {([
                    { label: 'HP',    raw: live.rawStats.health,      mult: live.raceBonus.health,           gearKey: 'health' as const },
                    { label: 'Str',   raw: live.rawStats.strength,    mult: live.raceBonus.strength,         gearKey: 'strength' as const },
                    { label: 'End',   raw: live.rawStats.endurance,   mult: live.raceBonus.endurance,        gearKey: 'endurance' as const },
                    { label: 'Init',  raw: live.rawStats.initiative,  mult: live.raceBonus.initiative,       gearKey: 'initiative' as const },
                    { label: 'Dodge', raw: live.rawStats.dodge,       mult: live.raceBonus.avoidance,        gearKey: 'dodge' as const },
                    { label: 'Learn', raw: live.rawStats.learning,    mult: live.raceBonus.learningCapacity, gearKey: 'learning' as const },
                    { label: 'Luck',  raw: live.rawStats.luck,        mult: live.raceBonus.luck,             gearKey: 'luck' as const },
                    { label: 'Disc',  raw: live.rawStats.discipline,  mult: live.raceBonus.discipline,       gearKey: 'discipline' as const },
                    { label: 'Lead',  raw: live.rawStats.leadership,  mult: live.raceBonus.leadership,       gearKey: 'leadership' as const },
                    { label: 'Prov',  raw: live.rawStats.provocation, mult: live.raceBonus.provocation,      gearKey: 'provocation' as const },
                  ]).map(({ label, raw, mult, gearKey }) => {
                    const pct = Math.round((mult - 1) * 100);
                    const sign = pct >= 0 ? '+' : '';
                    const multColor = pct > 0 ? '#5cb87a' : pct < 0 ? '#c0392b' : 'var(--color-text-secondary)';
                    const gear = live.gearStats?.[gearKey] ?? 0;
                    const gearColor = gear > 0 ? '#5cb87a' : gear < 0 ? '#c0392b' : 'var(--color-text-secondary)';
                    const total = Math.floor(raw * mult) + gear;
                    return (
                      <tr key={label} className={styles.sbRow}>
                        <td className={styles.sbLabel}>{label}</td>
                        <td className={styles.sbPoints}>{raw}</td>
                        <td className={styles.sbMult} style={{ color: multColor }}>{sign}{pct}%</td>
                        <td className={styles.sbMult} style={{ color: gearColor }}>{gear > 0 ? `+${gear}` : gear !== 0 ? gear : '—'}</td>
                        <td className={styles.sbTotal}>{total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Weapon Proficiencies */}
          <div className={styles.panel}>
            <div className={styles.panelTitle}>Weapon Proficiencies</div>
            {live.rawWeaponSkills && live.raceBonus?.weaponSkillBonus ? (
              <table className={styles.statBreakdownTable}>
                <thead>
                  <tr>
                    <th className={styles.sbHeader}>Weapon</th>
                    <th className={styles.sbHeader}>Base</th>
                    <th className={styles.sbHeader}>Racial</th>
                    <th className={styles.sbHeader}>Gear Bonus</th>
                    <th className={styles.sbHeader}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(['axe', 'sword', 'mace', 'staff', 'dagger', 'chain', 'shield'] as const).map((skill) => {
                    const raw = live.rawWeaponSkills![skill] ?? 0;
                    const raceMultiplier = live.raceBonus?.weaponSkillBonus![skill] ?? 1;
                    const withRace = raw * raceMultiplier;
                    const total = live.weaponSkills[skill] ?? 0;
                    const gearBonus = Math.round(total - withRace);
                    const racePct = Math.round((raceMultiplier - 1) * 100);
                    const raceColor = racePct > 0 ? '#5cb87a' : racePct < 0 ? '#c0392b' : 'var(--color-text-secondary)';
                    const gearColor = gearBonus > 0 ? '#5cb87a' : gearBonus < 0 ? '#c0392b' : 'var(--color-text-secondary)';
                    return (
                      <tr key={skill} className={styles.sbRow}>
                        <td className={styles.sbLabel}>{SKILL_LABEL[skill] ?? skill}</td>
                        <td className={styles.sbPoints}>{raw}</td>
                        <td className={styles.sbMult} style={{ color: raceColor }}>
                          {racePct > 0 ? '+' : ''}{racePct}%
                        </td>
                        <td className={styles.sbMult} style={{ color: gearColor }}>
                          {gearBonus > 0 ? '+' : ''}{gearBonus}
                        </td>
                        <td className={styles.sbTotal}>{Math.round(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className={styles.weaponProfGrid}>
                {Object.entries(live.weaponSkills)
                  .filter(([k]) => k !== 'unarmed')
                  .map(([k, v]) => (
                    <div key={k} className={styles.profRow}>
                      <span className={styles.profLabel}>{SKILL_LABEL[k] ?? k}</span>
                      <SkillBar value={v as number} />
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Combat Estimates */}
          <div className={styles.panel}>
            <div className={styles.panelTitle}>Combat Estimates</div>
            <div className={styles.estimatesNote}>These are a estimate, since opponent's stats varie, and other factors can affect the outcome.</div>
            <div className={styles.estimatesGrid}>
              <div className={styles.estRow}>
                <span className={styles.estLabel}>~Rounds in Battle</span>
                <span className={styles.estValue}>~{estBattleRounds}</span>
              </div>
              <div className={styles.estRow}>
                <span className={styles.estLabel}>~Dodge Chance</span>
                <span className={styles.estValue}>{estDodgePct}%</span>
              </div>
              {estBlockPct !== null && (
                <div className={styles.estRow}>
                  <span className={styles.estLabel}>~Block Chance</span>
                  <span className={styles.estValue}>{estBlockPct}%</span>
                </div>
              )}
              <div className={styles.estRow}>
                <span className={styles.estLabel}>Crit Chance</span>
                <span className={styles.estValue}>~{live.totalCritChance ?? 0}%</span>
              </div>
              <div className={styles.estRow}>
                <span className={styles.estLabel}>Crit Damage</span>
                <span className={styles.estValue}>
                  {(() => {
                    const base = live.totalCritDamage ?? 0;
                    const racial = live.racialCritDamage ?? 0;
                    const orcBonus = live.race?.toLowerCase() === 'orc' ? 7 : 0;
                    const max = base + racial + orcBonus;
                    return racial > 0 ? `~${base}% – ~${max}%` : `~${base}%`;
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BATTLE RECORD */}
      {s && (
        <div className={styles.recordSection}>
          <div className={styles.sectionTitle}>Battle Record</div>
          <div className={styles.recordGrid}>
            {[
              { label: 'Tournament', ...tournament },
              { label: 'Duel',       ...duel       },
              { label: 'Team',       ...team       },
              { label: 'PvE',        ...pve        },
              { label: 'Contract',   ...contract   },
            ].map(({ label, wins, losses, rate }) => (
              <div key={label} className={styles.recordCard}>
                <div className={styles.recordLabel}>{label}</div>
                <div className={styles.recordWL}>
                  <span className={styles.recordWins}>{wins}W</span>
                  <span className={styles.recordSep}>/</span>
                  <span className={styles.recordLosses}>{losses}L</span>
                </div>
                <div className={styles.recordRateBar}>
                  <div className={styles.recordRateFill} style={{ width: `${rate}%` }} />
                </div>
                <div className={styles.recordRate}>{rate}%</div>
              </div>
            ))}
          </div>

          {(s.highestDamageDealt > 0 || s.mostCriticalHits > 0 || s.mostDodgedAttacks > 0) && (
            <div className={styles.personalRecords}>
              <div className={styles.prTitle}>Personal Records</div>
              <div className={styles.prGrid}>
                {[
                  { label: 'Highest Single Hit',       v: s.highestDamageDealt },
                  { label: 'Most Damage in a Battle',  v: s.mostDamageDone     },
                  { label: 'Most Damage Taken',        v: s.mostDamageTaken    },
                  { label: 'Most Crits in a Battle',   v: s.mostCriticalHits   },
                  { label: 'Most Dodges in a Battle',  v: s.mostDodgedAttacks  },
                  { label: 'Most Parries in a Battle', v: s.mostParries        },
                ].filter(({ v }) => v > 0).map(({ label, v }) => (
                  <div key={label} className={styles.prItem}>
                    <span className={styles.prLabel}>{label}</span>
                    <span className={styles.prValue}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileView;
